"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ProductCardList from "@/components/ProductCardList";
import ProductCardListExpandableFooter from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/ProductCardListExpandableFooter";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import {
  CommonVariants,
  ElementType,
  FormatSubType,
} from "@/constants/tagular/main";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import type {
  Category,
  ProductCardListBySearchTabProps,
  Subcategory,
} from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/types";
import { CategoryTabBar } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/CategoryTabBar";
import { SubcategoryTabBar } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/SubcategoryTabBar";
import { createSearchTabValue } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/createSearchTabValue";
import { resolveExpandedSearchCtaUrl } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/resolveExpandedSearchCtaUrl";
import useIsMobile from "@/hooks/useIsMobile";
import { sanitizeTabValue } from "@/utils/sanitizeTabValue";
import { cn } from "@/lib/utils";
import { Flame } from "lucide-react";
import useHomepageTrendingCourseUpdate from "@/monarch/rules/homepageTrendingCourseUpdate/useHomepageTrendingCourseUpdate";
import { getFilteredTrendingCategories } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/getFilteredTrendingCategories";
import { resolveSubcategoryForCategory } from "@/components/ProductCardListWrapper/utils/resolveSubcategoryForCategory";

function ProductCardListBySearchTabClient({
  title,
  className,
  categories,
  location,
  cardFormatSubtype = FormatSubType.HorizontallyStacked,
}: ProductCardListBySearchTabProps) {
  const trendingTabs = useHomepageTrendingCourseUpdate();
  const categoryKeyOrderKey =
    trendingTabs.data.categoryKeyOrder?.join("\0") ?? "";

  const filteredCategories = useMemo(
    () => getFilteredTrendingCategories(categories, trendingTabs),
    [
      categories,
      trendingTabs.variant,
      trendingTabs.data.prioritizeCoursesFirst,
      categoryKeyOrderKey,
    ],
  );

  const [activeCategory, setActiveCategory] = useState<Category | undefined>(
    undefined,
  );
  const activeCategoryRef = useRef(activeCategory);
  activeCategoryRef.current = activeCategory;

  const [activeSubcategory, setActiveSubcategory] = useState<
    Subcategory | undefined
  >(undefined);

  const isMobile = useIsMobile();

  useEffect(() => {
    const first = filteredCategories[0];
    const orderSettled = !trendingTabs.isLoading;
    const prevCat = activeCategoryRef.current;

    if (!orderSettled) {
      if (!first) {
        setActiveCategory(undefined);
        setActiveSubcategory(undefined);
        return;
      }
      if (prevCat) {
        const stillThere = filteredCategories.find(
          (c) => c.categoryLabel === prevCat.categoryLabel,
        );
        setActiveCategory(stillThere);
        setActiveSubcategory((prevSub) =>
          resolveSubcategoryForCategory(prevSub, stillThere),
        );
        return;
      }
      setActiveCategory(undefined);
      setActiveSubcategory(undefined);
      return;
    }

    if (!first) {
      setActiveCategory(undefined);
      setActiveSubcategory(undefined);
      return;
    }

    const stillThere = prevCat
      ? filteredCategories.find(
          (c) => c.categoryLabel === prevCat.categoryLabel,
        )
      : undefined;
    const next = stillThere ?? first;

    setActiveCategory(next);
    setActiveSubcategory((prevSub) =>
      resolveSubcategoryForCategory(prevSub, next),
    );
  }, [filteredCategories, trendingTabs.isLoading]);

  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
    {},
  );

  const createAccordionKey = useCallback(
    (categoryLabel: string, subcategoryLabel: string) => {
      return `${createSearchTabValue(categoryLabel, location)}::${sanitizeTabValue(
        subcategoryLabel,
      )}`;
    },
    [location],
  );

  const handleCategoryChange = (sanitizedValue: string) => {
    setOpenAccordions({});
    const category = filteredCategories?.find(
      (cat) =>
        createSearchTabValue(cat.categoryLabel, location) === sanitizedValue,
    );
    setActiveCategory(category);
    setActiveSubcategory((prev) =>
      resolveSubcategoryForCategory(prev, category),
    );
  };

  const hasMoreProducts = (products: unknown[]) => {
    const initialDisplayCount = 4;
    return Array.isArray(products) && products.length > initialDisplayCount;
  };

  const shouldShowAccordion = (subcategory: Subcategory | undefined) => {
    return hasMoreProducts(subcategory?.products || []);
  };

  const generateEventData = useCallback(
    (text: string) => {
      const webElement: ElementClicked["webElement"] = {
        elementType: ElementType.Link,
        location: location,
        position: "1",
        text: text,
        variant: activeCategory
          ? translateVariant(activeCategory.categoryLabel)
          : CommonVariants.AllProductsMixed,
        htmlId: "view-more-link",
      };
      return { webElement };
    },
    [activeCategory, location],
  );

  const sendViewedEvent = useCallback(
    (title: string) => {
      const event = makeEvent<ElementViewed, EVENTS>(generateEventData(title));
      event<ElementViewed>(EVENTS.ElementViewed)();
    },
    [generateEventData],
  );

  const sendClickedEvent = useCallback(
    (title: string) => {
      const event = makeEvent<ElementClicked, EVENTS>(generateEventData(title));
      event<ElementClicked>(EVENTS.ElementClicked)();
    },
    [generateEventData],
  );

  return (
    <div
      className={cn(
        "py-8 lg:py-20 mx-break-out flex justify-center bg-putty-100",
        className,
      )}
    >
      <div className="container">
        <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xl lg:text-3xl font-bold mb-4">
          {trendingTabs.data.showTrendingFlameIcon && (
            <span className="inline-flex shrink-0 items-center">
              <Flame
                className="size-7 text-[#E85D32] lg:size-9"
                strokeWidth={1.75}
                aria-hidden
              />
            </span>
          )}
          <span>{title}</span>
        </h2>

        <Tabs
          value={
            activeCategory
              ? createSearchTabValue(activeCategory.categoryLabel, location)
              : undefined
          }
          onValueChange={handleCategoryChange}
        >
          <CategoryTabBar categories={filteredCategories} location={location} />

          {activeCategory ? (
            <TabsContent
              value={createSearchTabValue(
                activeCategory.categoryLabel,
                location,
              )}
              className={cn("p-4 mt-0 px-0 rounded-b-xl")}
            >
              <Tabs
                key={createSearchTabValue(
                  activeCategory.categoryLabel,
                  location,
                )}
                value={
                  activeSubcategory
                    ? sanitizeTabValue(activeSubcategory.subcategoryLabel)
                    : undefined
                }
                onValueChange={(sanitizedSubVal) => {
                  const originalSubcategory =
                    activeCategory.subcategories?.find(
                      (sub) =>
                        sanitizeTabValue(sub.subcategoryLabel) ===
                        sanitizedSubVal,
                    );
                  if (originalSubcategory) {
                    setActiveSubcategory(originalSubcategory);
                  }
                }}
              >
                <SubcategoryTabBar
                  subcategories={activeCategory.subcategories}
                  categoryLabel={activeCategory.categoryLabel}
                >
                  {(subcategory) => {
                    const accordionKey = createAccordionKey(
                      activeCategory.categoryLabel,
                      subcategory.subcategoryLabel,
                    );
                    const isAccordionOpen =
                      openAccordions[accordionKey] ?? false;
                    const accordionTitle = isAccordionOpen
                      ? `Show less ${activeCategory.categoryLabel.toLowerCase()}`
                      : `View more ${activeCategory.categoryLabel.toLowerCase()}`;
                    const allProducts = subcategory.products || [];
                    const products = isMobile
                      ? allProducts?.slice(0, 1)
                      : allProducts?.slice(0, 4) || [];
                    const subProducts = isMobile
                      ? subcategory.products?.slice(4, 8)
                      : subcategory.products?.slice(4) || [];

                    return (
                      <>
                        <ProductCardList
                          products={products}
                          location={location}
                          cardFormatSubType={cardFormatSubtype}
                          shouldScroll={false}
                        />
                        {shouldShowAccordion(subcategory) && (
                          <ProductCardListExpandableFooter
                            isExpanded={isAccordionOpen}
                            onToggle={(label) => {
                              setOpenAccordions((prev) => ({
                                ...prev,
                                [accordionKey]: !isAccordionOpen,
                              }));
                              sendClickedEvent(label);
                            }}
                            toggleLabel={accordionTitle}
                            searchCtaLabel="Explore more learning options."
                            searchCtaUrl={resolveExpandedSearchCtaUrl(
                              activeCategory,
                              subcategory,
                            )}
                            location={location}
                            onViewed={(label) => sendViewedEvent(label)}
                          >
                            <ProductCardList
                              className="pt-0"
                              products={subProducts || []}
                              location={location}
                              shouldScroll={false}
                            />
                          </ProductCardListExpandableFooter>
                        )}
                      </>
                    );
                  }}
                </SubcategoryTabBar>
              </Tabs>
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
}

export default ProductCardListBySearchTabClient;
